import { Logo } from '@myhome/ui/brand/logo';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@myhome/ui/components/form';
import { Input } from '@myhome/ui/components/input';
import { PasswordInput } from '@myhome/ui/patterns/password-input';
import { StatusBanner } from '@myhome/ui/patterns/status-banner';
import type * as React from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

/**
 * The frame every signed-out back-office screen shares: indigo page, logo,
 * one light panel with the heading and the form (DESIGN.md: indigo for
 * chrome, light surfaces for forms).
 */
export function AuthScreen({
  title,
  description,
  children,
}: {
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-navy-900 px-4 py-8">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex justify-center">
          <Logo height={56} tile={false} />
        </div>
        <main className="flex flex-col gap-4 rounded-(--radius-card) bg-surface p-5">
          <div>
            <h1 className="font-heading text-title font-semibold text-navy-900">{title}</h1>
            {description === undefined ? null : (
              <p className="mt-1 text-meta text-neutral-600">{description}</p>
            )}
          </div>
          {children}
        </main>
      </div>
    </div>
  );
}

interface FieldProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  description?: string;
}

export function EmailField<T extends FieldValues>({ control, name, label }: FieldProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input type="email" autoComplete="username" autoCapitalize="none" spellCheck={false} {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function PasswordField<T extends FieldValues>({
  control,
  name,
  label,
  description,
  autoComplete,
}: FieldProps<T> & { autoComplete: 'current-password' | 'new-password' }) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <PasswordInput autoComplete={autoComplete} {...field} />
          </FormControl>
          {description === undefined ? null : <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function FormError({ message }: { message: string | undefined }) {
  if (message === undefined) return null;
  return <StatusBanner tone="danger" title={message} />;
}
